import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  BellPlus, BellRing, Check, Loader2, CalendarClock, Clock, Tag, Pencil, Trash2, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  addContactReminder,
  getContactReminders,
  updateContactReminder,
  deleteContactReminder,
} from '../../../api/contact';
import {
  formatMelbourneDatePart,
  formatMelbourneTimePart,
  formatMelbourneDateTime,
  melbourneWallTimeToUtcISOString,
} from '../../../utils/melbourneTime';

/**
 * "Remind me to call again" preset picker. Drops under any note input so an agent
 * can schedule a follow-up call in one tap: 1 day / 1 week / 1 / 3 / 6 / 12 months
 * or a custom date. Each selection creates a ContactReminder against the contact;
 * the reminder then surfaces in the agent's "Who to Call" list once it's due.
 *
 * A reminder can also be moved or removed from here. "When setting a reminder, if
 * you got the date wrong or want to delete it you should be able to" (Simon
 * Abbott, CRM-2026-00074, 21 Sep 2026). Until then the only thing on offer after
 * saving was "Add another", so a mis-typed date could only be fixed by booking a
 * second reminder on top of the wrong one. The panel now lists this agent's open
 * reminders for the contact (not just the one set in this session, so a date
 * entered yesterday is still fixable) with Change and Delete on each.
 *
 * Props:
 *   contactId       resolved CRM contact id the reminder attaches to (required to save)
 *   noteId          optional ContactNote id to link the reminder to (for call context)
 *   noteText        optional text copied into the reminder description (shows in Who-to-Call)
 *   onCreated(rem)  optional callback with the created reminder
 *   onChanged(rem)  optional callback with the moved reminder
 *   onDeleted(id)   optional callback with the deleted reminder's id
 *   label           heading text (default "Remind me to call again")
 */

// Reason groups the agent picks so the reminder carries its intent into
// Who-to-Call / Tasks (Michael Piccoli, 2026-07-23). Values must match the
// ContactReminder.REASON_CHOICES on the backend.
const REASONS = [
  { key: 'contact', label: 'Contact' },
  { key: 'follow_up', label: 'Follow Up' },
  { key: 'appointment', label: 'Appointment' },
  { key: 'action_required', label: 'Action Required' },
  { key: 'review', label: 'Review' },
];

const PRESETS = [
  { key: '1d', label: '1 day', add: (d) => d.setDate(d.getDate() + 1) },
  { key: '1w', label: '1 week', add: (d) => d.setDate(d.getDate() + 7) },
  { key: '1m', label: '1 month', add: (d) => d.setMonth(d.getMonth() + 1) },
  { key: '3m', label: '3 months', add: (d) => d.setMonth(d.getMonth() + 3) },
  { key: '6m', label: '6 months', add: (d) => d.setMonth(d.getMonth() + 6) },
  { key: '12m', label: '12 months', add: (d) => d.setMonth(d.getMonth() + 12) },
];

// Titles this picker generates itself, e.g. "Follow Up (1 week)". Only a title in
// that shape gets rewritten when a reminder is moved. One written elsewhere (the
// appraisal series, a garage-sale call-back) says something the agent chose, and
// changing the date must not overwrite it.
const AUTO_TITLE = new RegExp(`^(${REASONS.map((r) => r.label).join('|')}) \\(.+\\)$`);

// Apply the preset's day offset, then pin to the agent's chosen call-back time
// (local). Keeps the due date stable regardless of when the preset was tapped.
const presetDueDate = (mutate, hours, minutes) => {
  const d = new Date();
  mutate(d);
  d.setHours(hours, minutes, 0, 0);
  return d;
};

const pad = (n) => String(n).padStart(2, '0');

// The agent picked a day and a time, and that is Melbourne wall-clock time, the same
// as every other scheduled thing in the CRM (Tasks buckets reminders by the
// agency's day, the due-reminder email renders them in agency time). Sending the
// browser's own local instant instead put a reminder set by an agent or PA working
// outside Melbourne hours away from the time they typed.
const toStoredISOString = (d) =>
  melbourneWallTimeToUtcISOString(
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  );

// Friendly "Tue, 27 Jun, 9:00 AM" for confirmation copy.
const formatDueDate = (d) =>
  d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

// Pull the first readable message out of a DRF error body instead of a generic
// one. An opaque failure here made the feature look broken.
const errorMessage = (e, fallback) => {
  const data = e?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    const first = data.detail || data.error
      || Object.values(data).flat().filter(Boolean)[0];
    if (first) return String(first);
  }
  return fallback;
};

const ReminderPresets = ({
  contactId,
  noteId = null,
  noteText = '',
  onCreated,
  onChange,
  onChanged,
  onDeleted,
  label = 'Remind me to call again',
}) => {
  const { user } = useSelector((state) => state.auth);
  const deferred = typeof onChange === 'function';

  const [saving, setSaving] = useState(null); // preset key currently saving
  const [scheduled, setScheduled] = useState(null); // { label, date }, deferred mode only
  const [showCustom, setShowCustom] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [time, setTime] = useState('09:00'); // call-back time of day applied to every preset
  const [reason, setReason] = useState('follow_up'); // why the reminder is being set

  // This agent's open reminders on the contact, so one set earlier is still
  // fixable. Saved reminders only, because deferred (draft) mode has nothing on record.
  const [reminders, setReminders] = useState([]);
  const [justSetId, setJustSetId] = useState(null); // the one created in this session
  const justSetIdRef = useRef(null);
  const [editingId, setEditingId] = useState(null); // reminder being moved
  const [confirmingId, setConfirmingId] = useState(null); // delete awaiting a second tap
  const [busyId, setBusyId] = useState(null); // reminder mid save/delete

  const reasonLabel = REASONS.find((r) => r.key === reason)?.label || 'Follow Up';
  const editing = reminders.find((r) => r.id === editingId) || null;
  const justSet = reminders.find((r) => r.id === justSetId) || null;
  // Everything the agent can still fix, newest booking last, minus the green
  // confirmation chip's own reminder and whichever one is open for editing.
  const otherReminders = reminders
    .filter((r) => r.id !== justSetId && r.id !== editingId)
    .slice(0, 5);

  const loadReminders = useCallback(async () => {
    if (!contactId || deferred) return;
    try {
      const res = await getContactReminders(contactId);
      const open = (res.data || []).filter((r) => (
        !r.is_completed
        // Only ever offer the agent their own reminders to move or delete; a
        // colleague's call-back on a shared contact is not theirs to change.
        // Compared as strings so an id that arrives as "26467" rather than
        // 26467 doesn't quietly hide every one of them.
        && (!user?.id || String(r.agent) === String(user.id))
      ));
      // Whatever the list says, the reminder just set in this session stays -
      // it is what the green confirmation is showing, and it must not blink out
      // from under the agent's Change/Delete buttons.
      setReminders((prev) => {
        const justSetRow = prev.find((r) => r.id === justSetIdRef.current);
        return justSetRow && !open.some((r) => r.id === justSetRow.id)
          ? [...open, justSetRow]
          : open;
      });
    } catch (e) {
      // A reminder list that won't load must not break note-taking. The picker
      // still works, it just can't offer the existing ones.
      console.error('Error loading reminders:', e);
    }
  }, [contactId, deferred, user?.id]);

  useEffect(() => { loadReminders(); }, [loadReminders]);

  const resetEditor = () => {
    setEditingId(null);
    setShowCustom(false);
    setCustomDate('');
    setTime('09:00');
    setReason('follow_up');
  };

  // Split the "HH:MM" time input into numbers for Date.setHours.
  const parseTime = () => {
    const [h, m] = time.split(':').map(Number);
    return {
      hours: Number.isFinite(h) ? h : 9,
      minutes: Number.isFinite(m) ? m : 0,
    };
  };

  const create = async (dueDate, presetLabel, key) => {
    if (onChange) {
      onChange({ date: dueDate, reason });
      setScheduled({ label: presetLabel, date: dueDate });
      setShowCustom(false);
      return;
    }

    if (!contactId) {
      toast.error('Save the contact first to set a reminder.');
      return;
    }
    setSaving(key);
    try {
      const res = await addContactReminder(contactId, {
        title: `${reasonLabel} (${presetLabel})`,
        reason,
        due_date: toStoredISOString(dueDate),
        description: noteText || '',
        note: noteId || undefined,
      });
      setReminders((prev) => [...prev.filter((r) => r.id !== res.data?.id), res.data]);
      justSetIdRef.current = res.data?.id ?? null;
      setJustSetId(res.data?.id ?? null);
      setShowCustom(false);
      toast.success(`Reminder set for ${formatDueDate(dueDate)}`);
      onCreated?.(res.data);
      // Booking a fresh call-back closes out this agent's overdue ones on the
      // contact (server side), so re-read rather than trust the local list.
      loadReminders();
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to set reminder'));
    } finally {
      setSaving(null);
    }
  };

  // Move an existing reminder to a new date/time (and reason, if it was changed).
  const saveEdit = async (dueDate, presetLabel, key) => {
    if (!editing) return;
    setSaving(key);
    setBusyId(editing.id);
    try {
      const payload = {
        reason,
        due_date: toStoredISOString(dueDate),
      };
      // Keep an agent-written title; refresh one this picker generated so Tasks
      // and Who-to-Call don't keep showing the date it used to be.
      if (AUTO_TITLE.test(editing.title || '')) {
        payload.title = `${reasonLabel} (${presetLabel})`;
      }
      const res = await updateContactReminder(contactId, editing.id, payload);
      setReminders((prev) => prev.map((r) => (r.id === editing.id ? res.data : r)));
      resetEditor();
      toast.success(`Reminder moved to ${formatDueDate(dueDate)}`);
      onChanged?.(res.data);
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to move reminder'));
    } finally {
      setSaving(null);
      setBusyId(null);
    }
  };

  const commit = (dueDate, presetLabel, key) => (
    editing ? saveEdit(dueDate, presetLabel, key) : create(dueDate, presetLabel, key)
  );

  const handlePreset = (p) => {
    const { hours, minutes } = parseTime();
    commit(presetDueDate(p.add, hours, minutes), p.label, p.key);
  };

  const handleCustom = () => {
    if (!customDate) return;
    // Date input gives YYYY-MM-DD (local); combine with the chosen call-back time.
    const d = new Date(`${customDate}T${time}:00`);
    if (Number.isNaN(d.getTime())) {
      toast.error('Pick a valid date and time');
      return;
    }
    commit(d, formatDueDate(d), 'custom');
  };

  // Open the picker on an existing reminder, prefilled with where it sits now so
  // a wrong date can be corrected rather than re-entered from scratch.
  const startEdit = (rem) => {
    setEditingId(rem.id);
    setConfirmingId(null);
    setReason(rem.reason || 'follow_up');
    setCustomDate(formatMelbourneDatePart(rem.due_date));
    setTime(formatMelbourneTimePart(rem.due_date) || '09:00');
    setShowCustom(true);
  };

  const remove = async (rem) => {
    // Deferred mode holds a draft, not a saved row, so just hand the parent a
    // cleared value back.
    if (deferred) {
      onChange(null);
      setScheduled(null);
      setConfirmingId(null);
      return;
    }
    setBusyId(rem.id);
    try {
      await deleteContactReminder(contactId, rem.id);
      setReminders((prev) => prev.filter((r) => r.id !== rem.id));
      if (justSetId === rem.id) {
        justSetIdRef.current = null;
        setJustSetId(null);
      }
      if (editingId === rem.id) resetEditor();
      setConfirmingId(null);
      toast.success('Reminder deleted');
      onDeleted?.(rem.id);
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to delete reminder'));
    } finally {
      setBusyId(null);
    }
  };

  // Change / Delete pair shown on the green chip and on each listed reminder.
  // Delete asks twice: a reminder is cheap to re-book but a silent one-tap
  // delete next to "Change" is too easy to hit by accident.
  const RowActions = ({ rem, tone = 'emerald' }) => {
    const confirming = confirmingId === (rem?.id ?? 'draft');
    const busy = rem && busyId === rem.id;
    const linkTone = tone === 'emerald'
      ? 'text-emerald-600 hover:text-emerald-800'
      : 'text-gray-500 hover:text-gray-700';

    if (confirming) {
      return (
        <span className="inline-flex items-center gap-2">
          <button
            type="button"
            onClick={() => remove(rem)}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-2 py-1 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Delete it
          </button>
          <button
            type="button"
            onClick={() => setConfirmingId(null)}
            disabled={busy}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
            title="Keep the reminder"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-2.5">
        {!deferred && rem && (
          <button
            type="button"
            onClick={() => startEdit(rem)}
            disabled={busy}
            className={`inline-flex items-center gap-1 text-xs font-semibold ${linkTone} hover:underline disabled:opacity-50`}
          >
            <Pencil className="h-3.5 w-3.5" />
            Change
          </button>
        )}
        {deferred && (
          <button
            type="button"
            onClick={() => { setScheduled(null); setShowCustom(false); }}
            className={`inline-flex items-center gap-1 text-xs font-semibold ${linkTone} hover:underline`}
          >
            <Pencil className="h-3.5 w-3.5" />
            Change
          </button>
        )}
        <button
          type="button"
          onClick={() => setConfirmingId(rem?.id ?? 'draft')}
          disabled={busy}
          className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700 hover:underline disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Delete
        </button>
      </span>
    );
  };

  // The green "it's booked" confirmation. Deferred mode has no saved reminder, so
  // it works off the local draft.
  const confirmation = deferred
    ? (scheduled && { dueLabel: formatDueDate(scheduled.date), rem: null })
    : (justSet && { dueLabel: formatMelbourneDateTime(justSet.due_date), rem: justSet });

  if (confirmation && !editingId) {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
            <Check className="h-4 w-4" />
            Call-back reminder set for {confirmation.dueLabel}
          </span>
          <span className="inline-flex items-center gap-3">
            <RowActions rem={confirmation.rem} />
            <button
              type="button"
              onClick={() => {
                justSetIdRef.current = null;
                setJustSetId(null);
                setScheduled(null);
                setConfirmingId(null);
                setShowCustom(false);
              }}
              className="text-sm font-medium text-emerald-600 hover:underline"
            >
              Add another
            </button>
          </span>
        </div>
        {otherReminders.length > 0 && (
          <ReminderList
            reminders={otherReminders}
            renderActions={(rem) => <RowActions rem={rem} tone="gray" />}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <div className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-gray-500">
            <BellPlus className="h-4 w-4 text-teal-500" />
            {editing ? 'Change this reminder' : label}
          </div>
          <label className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600">
            <Clock className="h-4 w-4 text-teal-500" />
            at
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
            />
          </label>
          {editing && (
            <button
              type="button"
              onClick={resetEditor}
              disabled={saving !== null}
              className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
          )}
        </div>
        {editing && (
          <p className="mb-2 text-xs text-gray-500">
            Currently set for {formatMelbourneDateTime(editing.due_date)}. Pick a new date
            below, or tap one of the presets to count from today.
          </p>
        )}
        <div className="mb-2">
          <div className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
            <Tag className="h-4 w-4 text-teal-500" />
            Reason
          </div>
          <div className="flex flex-wrap gap-1.5">
            {REASONS.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setReason(r.key)}
                disabled={saving !== null}
                className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                  reason === r.key
                    ? 'border-teal-400 bg-teal-500 text-white'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-teal-300 hover:text-teal-700'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => handlePreset(p)}
              disabled={saving !== null}
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-teal-300 hover:text-teal-700 disabled:opacity-50"
            >
              {saving === p.key && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {p.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowCustom((s) => !s)}
            disabled={saving !== null}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
              showCustom
                ? 'border-teal-300 bg-teal-50 text-teal-700'
                : 'border-gray-200 bg-white text-gray-700 hover:border-teal-300 hover:text-teal-700'
            }`}
          >
            <CalendarClock className="h-3.5 w-3.5" />
            Custom
          </button>
        </div>
        {showCustom && (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              className="rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
            />
            <button
              type="button"
              onClick={handleCustom}
              disabled={!customDate || saving !== null}
              className="inline-flex items-center gap-1 rounded-lg bg-teal-500 px-3 py-1.5 text-sm font-bold text-white hover:bg-teal-600 disabled:opacity-50"
            >
              {saving === 'custom'
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : (editing ? 'Save change' : 'Set')}
            </button>
          </div>
        )}
      </div>
      {otherReminders.length > 0 && (
        <ReminderList
          reminders={otherReminders}
          renderActions={(rem) => <RowActions rem={rem} tone="gray" />}
        />
      )}
    </div>
  );
};

// The agent's open reminders on this contact, so a date set on an earlier visit
// can still be corrected or dropped without hunting for it in Tasks.
const ReminderList = ({ reminders, renderActions }) => (
  <div className="rounded-xl border border-gray-100 bg-white px-3 py-2">
    <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      <BellRing className="h-3.5 w-3.5 text-gray-400" />
      Reminders you've set
    </div>
    <ul className="divide-y divide-gray-50">
      {reminders.map((rem) => (
        <li key={rem.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
          <span className="text-xs text-gray-600">
            <span className="font-semibold text-gray-800">{formatMelbourneDateTime(rem.due_date)}</span>
            {rem.reason_display ? ` · ${rem.reason_display}` : ''}
          </span>
          {renderActions(rem)}
        </li>
      ))}
    </ul>
  </div>
);

export default ReminderPresets;
